import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { WishlistContext } from "../../Context/WishlistContext";
import { MdClose } from "react-icons/md";
import { FiHeart, FiTrash2, FiArrowRight } from "react-icons/fi";
import { FaHeart } from "react-icons/fa";
import { currency } from "../../App";

const slugify = (text) =>
  text.toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "")
    .replace(/--+/g, "-")
    .replace(/^-+|-+$/g, "");

const WishlistPanel = () => {
  const { wishlist, wishlistOpen, setWishlistOpen, removeFromWishlist } = useContext(WishlistContext);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={() => setWishlistOpen(false)}
        className="fixed inset-0 z-[300] transition-all duration-300"
        style={{
          background: "rgba(0,0,0,0.45)",
          opacity: wishlistOpen ? 1 : 0,
          pointerEvents: wishlistOpen ? "auto" : "none",
        }}
      />

      {/* Slide-in panel */}
      <div
        className="fixed top-0 right-0 h-full w-full max-w-[400px] bg-white z-[301] flex flex-col"
        style={{
          boxShadow: "-8px 0 40px rgba(124,58,237,0.12)",
          transform: wishlistOpen ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.4s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-purple-100"
          style={{ background: "linear-gradient(135deg,#F6F1FE,#F6F1FE)" }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center shadow-md"
              style={{ background: "linear-gradient(135deg,#8353C6,#9E79D2)" }}>
              <FaHeart size={15} color="white" />
            </div>
            <div>
              <h3 className="font-black text-gray-900 text-base tracking-tight">My Wishlist</h3>
              <p className="text-[11px] font-medium" style={{ color: "#8353C6" }}>
                {wishlist.length} {wishlist.length === 1 ? "item" : "items"} saved
              </p>
            </div>
          </div>
          <button
            onClick={() => setWishlistOpen(false)}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-purple-100 transition-colors"
          >
            <MdClose size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Accent bar */}
        <div className="h-[3px]" style={{ background: "linear-gradient(90deg,#8353C6,#9E79D2,#6939AC)" }} />

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4" style={{ scrollbarWidth: "thin", scrollbarColor: "#EEE5FA transparent" }}>
          {wishlist.length === 0 ? (
            /* Empty state */
            <div className="flex flex-col items-center justify-center h-full text-center gap-5 py-16">
              <div className="relative">
                <div className="w-24 h-24 rounded-full flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg,#F6F1FE,#EEE5FA)" }}>
                  <FiHeart size={36} style={{ color: "#8353C6" }} />
                </div>
                <div className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center bg-white shadow-md border border-purple-100">
                  <span className="text-base">✨</span>
                </div>
              </div>
              <div>
                <p className="font-bold text-gray-800 text-base mb-1">Nothing saved yet</p>
                <p className="text-gray-400 text-sm leading-relaxed max-w-[220px]">
                  Tap the ♡ on any product card to add it here
                </p>
              </div>
              <Link
                to="/"
                onClick={() => setWishlistOpen(false)}
                className="inline-flex items-center gap-2 text-sm font-bold px-6 py-2.5 rounded-full text-white shadow-md transition-opacity hover:opacity-90"
                style={{ background: "linear-gradient(135deg,#8353C6,#9E79D2)" }}
              >
                Browse Products <FiArrowRight size={13} />
              </Link>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {wishlist.map((item, i) => (
                <div
                  key={item.id}
                  className="flex gap-3 p-3 rounded-2xl border border-purple-50 transition-all duration-200 hover:border-purple-200 hover:shadow-sm group"
                  style={{ background: "#F6F1FE" }}
                >
                  {/* Product image */}
                  <Link
                    to={`/product/${item.id}/${slugify(item.name)}`}
                    onClick={() => { setWishlistOpen(false); window.scrollTo(0, 0); }}
                    className="shrink-0"
                  >
                    <div className="w-[80px] h-[80px] rounded-xl overflow-hidden border border-purple-100 bg-purple-50 flex items-center justify-center">
                      <img
                        crossOrigin="anonymous"
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          e.currentTarget.parentElement.innerHTML =
                            '<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;color:#D6C3F0;font-size:28px;">🧵</div>';
                        }}
                      />
                    </div>
                  </Link>

                  {/* Info */}
                  <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                    <Link
                      to={`/product/${item.id}/${slugify(item.name)}`}
                      onClick={() => { setWishlistOpen(false); window.scrollTo(0, 0); }}
                    >
                      <p className="text-sm font-semibold text-gray-800 line-clamp-2 leading-snug hover:text-purple-700 transition-colors">
                        {item.name}
                      </p>
                    </Link>
                    <p className="text-sm font-black mt-1" style={{ color: "#8353C6" }}>
                      {currency}{item.new_price}
                    </p>
                  </div>

                  {/* Remove */}
                  <button
                    onClick={() => removeFromWishlist(item.id)}
                    className="shrink-0 self-start mt-0.5 w-7 h-7 rounded-full flex items-center justify-center text-gray-300 hover:text-red-400 hover:bg-red-50 transition-all duration-200"
                    title="Remove"
                  >
                    <FiTrash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {wishlist.length > 0 && (
          <div className="px-4 py-4 border-t border-purple-100"
            style={{ background: "linear-gradient(135deg,#F6F1FE,#F6F1FE)" }}>
            <p className="text-center text-xs text-gray-400 mb-3">
              {wishlist.length} {wishlist.length === 1 ? "product" : "products"} in your wishlist
            </p>
            <Link
              to="/"
              onClick={() => setWishlistOpen(false)}
              className="flex items-center justify-center gap-2 w-full text-sm font-bold py-3 rounded-full text-white shadow-md hover:opacity-90 transition-opacity"
              style={{ background: "linear-gradient(135deg,#8353C6,#9E79D2)" }}
            >
              Continue Shopping <FiArrowRight size={13} />
            </Link>
          </div>
        )}
      </div>
    </>
  );
};

export default WishlistPanel;
